/**
 * HMAC-secured worker profile fetch via support-services workerSecure/workerInfo.
 * Mirrors IM Time mobile: sign_key from Remote Config sign_info (imWarehouse group).
 *
 * Headers: worker_id, timestamp, signature
 * message = worker_id + ":" + timestamp
 * signature = hex(HMAC-SHA256(sign_key, message)) — no Base64 decode of key
 */

import CryptoJS from "crypto-js";
import { getSignInfoFromRc } from "./rconfig";

let cachedWorkerHmacSignKey = null;
let cachedWorkerHmacSkewMs = null;
let workerHmacSignKeyPromise = null;

const getWorkerInfoUrl = () => process.env.REACT_APP_WORKER_INFO_URL;

/**
 * Load HMAC sign_key (and sign_skew_ms) from Remote Config sign_info.
 * Cached in memory for the session.
 */
export const getWorkerHmacSignKey = async () => {
  if (cachedWorkerHmacSignKey) {
    return cachedWorkerHmacSignKey;
  }
  if (workerHmacSignKeyPromise) {
    return workerHmacSignKeyPromise;
  }

  workerHmacSignKeyPromise = (async () => {
    const signInfo = await getSignInfoFromRc();
    const signKey = signInfo && signInfo.sign_key;
    if (!signKey) {
      throw new Error("sign_key missing on Remote Config sign_info");
    }
    cachedWorkerHmacSignKey = String(signKey);
    if (signInfo.sign_skew_ms != null && signInfo.sign_skew_ms !== "") {
      const skew = Number(signInfo.sign_skew_ms);
      cachedWorkerHmacSkewMs = Number.isNaN(skew) ? null : skew;
    }
    console.log("services > getWorkerHmacSignKey > loaded from Remote Config.");
    return cachedWorkerHmacSignKey;
  })();

  try {
    return await workerHmacSignKeyPromise;
  } catch (error) {
    workerHmacSignKeyPromise = null;
    console.error(
      "services > getWorkerHmacSignKey > Remote Config read failed.",
      error
    );
    throw error;
  } finally {
    if (cachedWorkerHmacSignKey) {
      workerHmacSignKeyPromise = null;
    }
  }
};

/** Cached HMAC timestamp skew from RC sign_info.sign_skew_ms (ms), if loaded. */
export const getWorkerHmacSkewMs = () => cachedWorkerHmacSkewMs;

const signWorkerInfoRequest = (workerId, signKey) => {
  const timestamp = String(Date.now());
  const message = `${workerId}:${timestamp}`;
  const signature = CryptoJS.HmacSHA256(message, signKey)
    .toString(CryptoJS.enc.Hex)
    .toLowerCase();
  return { timestamp, signature };
};

const isWorkerNotFoundResponse = (status, body) => {
  const message = (body?.message || "").toLowerCase();
  return (
    message === "not a valid worker id" ||
    message.includes("worker not found") ||
    (status === 404 && body?.status !== "success")
  );
};

const isAuthFailureResponse = (status, body) => {
  const message = (body?.message || "").toLowerCase();
  const authStatus =
    body?.status === "Unauthorized" ||
    body?.error === "Unauthorized" ||
    body?.status === "failure" ||
    status === 401 ||
    status === 403;
  return (
    authStatus &&
    (message.includes("signature") ||
      message.includes("timestamp") ||
      message.includes("expired") ||
      message.includes("invalid") ||
      message.includes("unauthorized") ||
      message.includes("hmac") ||
      !isWorkerNotFoundResponse(status, body))
  );
};

/**
 * @param {string} workerId
 * @return {Promise<{ ok: boolean, status: number, body: object }>}
 */
export const fetchWorkerInfo = async (workerId) => {
  const id = String(workerId);
  const url = getWorkerInfoUrl();
  if (!url) {
    throw new Error("REACT_APP_WORKER_INFO_URL is missing from env config");
  }
  const signKey = await getWorkerHmacSignKey();
  const { timestamp, signature } = signWorkerInfoRequest(id, signKey);

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      worker_id: id,
      timestamp,
      signature,
    },
  });
  let body = {};
  try {
    body = await response.json();
  } catch (parseErr) {
    console.error("fetchWorkerInfo: failed to parse JSON response");
  }
  return { ok: response.ok, status: response.status, body };
};

/**
 * DocumentSnapshot-like object for existing callers
 * (data.exists / data.data() / data.id).
 */
export const toWorkerSnapshot = (workerId, worker) => {
  const exists = !!worker;
  const id = (worker && (worker.workerId || worker.id)) || String(workerId);
  return {
    exists,
    id,
    data: () => (exists ? worker : undefined),
  };
};

/**
 * Fetch worker via HMAC HTTP; returns DocumentSnapshot-like result.
 * @param {string} empId
 */
export const fetchWorkerSnapshot = async (empId) => {
  const { ok, status, body } = await fetchWorkerInfo(empId);

  if (
    isAuthFailureResponse(status, body) &&
    !isWorkerNotFoundResponse(status, body)
  ) {
    throw new Error(
      body?.message || `workerInfo auth failed with status ${status}`
    );
  }

  if (isWorkerNotFoundResponse(status, body)) {
    return toWorkerSnapshot(empId, null);
  }

  if (!ok || body?.status !== "success" || !body?.data?.worker) {
    throw new Error(
      body?.message || `workerInfo failed with status ${status}`
    );
  }

  return toWorkerSnapshot(body.data.workerId || empId, body.data.worker);
};
